import type {ReactElement} from 'react';
import React from 'react';
import type {ColorPickerProps, ColorStorage} from '@Components/color-picker-v2';
import {ColorPicker} from '@Components/color-picker-v2';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useColorsOnMyDesign} from '@Hooks/poster-editor/useColorsOnMyDesign';
import {updatePickingMode} from '@Components/poster-editor/poster-editor-reducer';
import {Canvas} from '@postermywall/fabricjs-2';
import {useAppDispatch} from '@/hooks';
import styles from './poster-editing-base-color-panel.module.scss';
import {Button, Size, Type} from '@Components/button';
import {ColorFillPropertyType} from '@Components/color-fill';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {rgbaToHexString} from '@Utils/color.util';

export interface ColorPanelFooterOption {
  show: boolean;
  initialColor: ColorStorage[];
  initialType?: ColorFillPropertyType;
  buttonText: string;
  onButtonClick: (currentColor: ColorStorage[]) => void;
}

type PosterEditingBaseColorPanelProps = Omit<
  ColorPickerProps,
  'onChangeColorPickerMode' | 'onAddBrandAssetClicked' | 'onMyDesign'
> & {
  footerOption?: ColorPanelFooterOption;
};

export function PosterEditingBaseColorPanel({isAlphaDisabled = false, footerOption, ...props}: PosterEditingBaseColorPanelProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const colorsOnMyDesign = useColorsOnMyDesign();

  const onTogglePickerMode = (state: boolean): void => {
    dispatch(updatePickingMode(state));
  };
  const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
  if (!(canvas instanceof Canvas)) {
    return null;
  }

  const prepareBgfromInitialColor = (color: ColorStorage[], type?: ColorFillPropertyType): string => {
    if (!type) {
      return '';
    }
    if (type === ColorFillPropertyType.SOLID) return rgbaToHexString(color[0].color);
    const color1 = rgbaToHexString(color[1].color);
    const color2 = rgbaToHexString(color[0].color);

    if (type === ColorFillPropertyType.LINEAR) return `linear-gradient(${color1}, ${color2})`;
    if (type === ColorFillPropertyType.RADIAL) return `radial-gradient(${color1}, ${color2})`;

    return '';
  };

  const showFooter = (): boolean => {
    return !(!footerOption || (footerOption && !footerOption.show));
  };

  const getFooter = (): ReactElement | null => {
    if (!showFooter()) {
      return null;
    }
    if (!footerOption) {
      return null;
    }
    return (
      <div className={`${styles.footer} spacing-p-5 flex-h-row gap-4 flex-items-center`}>
        <div className="flex-h-row flex-items-center gap-1">
          <div className={`${styles.colorBox} radius-round border-s-dark`} style={{background: prepareBgfromInitialColor(footerOption.initialColor, footerOption.initialType)}} />
          <Icon icon="icon-arrow-right" size={IconSize.SIZE_ICON_16} />
          <div className={`${styles.colorBox} radius-round border-s-dark`} style={{background: prepareBgfromInitialColor(props.color, props.type)}} />
        </div>
        <Button
          customClasses="_full-width"
          size={Size.SMALL}
          type={Type.PRIMARY}
          text={footerOption.buttonText}
          onClick={(): void => {
            footerOption.onButtonClick(props.color);
          }}
        />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <ColorPicker
        id={props.id}
        className={props.className ? `${props.className} ${showFooter() ? 'spacing-p-b-4' : ''}` : showFooter() ? 'spacing-p-b-4' : ''}
        color={props.color}
        onMyDesign={colorsOnMyDesign}
        type={props.type}
        isGradientEnabled={props.isGradientEnabled}
        onChangeColor={props.onChangeColor}
        onChangeEnded={props.onChangeEnded}
        onChangeType={props.onChangeType}
        onAddBrandAssetClicked={(): void => {
          void onAddBrandAssetClickedHandler();
        }}
        onChangeColorPickerMode={onTogglePickerMode}
        addDelayToOnChangeColor={props.addDelayToOnChangeColor}
        showSmallBrandDropdownHeader={false}
        isAlphaDisabled={isAlphaDisabled}
      />
      {getFooter()}
    </div>
  );
}
